import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { apiErrorMessage } from "../lib/errors";

export default function ResetPassword() {
  const { refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!token) {
      toast.error("Missing reset token");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      await refresh();
      toast.success("Password updated — you're signed in");
      navigate("/app");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Reset failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain relative flex items-center justify-center px-6">
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <div className="w-8 h-8 border border-[#00D4FF] rotate-45 flex items-center justify-center">
            <span className="-rotate-45 text-[#00D4FF] font-mono text-xs font-bold">M</span>
          </div>
          <div>
            <div className="font-display text-lg leading-none">Martingale</div>
            <div className="overline leading-none mt-1">PLAYBOOK·TERMINAL</div>
          </div>
        </Link>

        <div className="overline mb-2">RESET</div>
        <h1 className="font-display text-4xl mb-8">Pick a new password.</h1>

        {!token ? (
          <div className="border border-amber-500/40 bg-amber-500/5 p-6 text-sm text-amber-300">
            The reset link is missing or malformed. Request a new one from{" "}
            <Link to="/forgot-password" className="underline">Forgot password</Link>.
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="space-y-5 border border-zinc-800 p-8 bg-zinc-950/40"
          >
            <div>
              <label className="overline block mb-2">NEW PASSWORD</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password"
                className="w-full bg-transparent border border-zinc-800 px-3 py-3 mono text-sm focus:outline-none focus:border-[#00D4FF]"
                placeholder="at least 6 characters"
              />
            </div>
            <div>
              <label className="overline block mb-2">CONFIRM</label>
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm"
                className="w-full bg-transparent border border-zinc-800 px-3 py-3 mono text-sm focus:outline-none focus:border-[#00D4FF]"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="w-full py-3 bg-[#00D4FF] text-black font-mono uppercase tracking-widest text-xs disabled:opacity-50 hover:brightness-110"
            >
              {loading ? "Updating..." : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}